import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import BatteryStatusPill from 'pages/MaintenancePage/components/BatteryCard/components/BatteryStatusPill';
import BatteryStatusControl from 'pages/MaintenancePage/components/BatteryStatusControl';
import { DEFAULT_LIFECYCLE_STATUS } from 'pages/MaintenancePage/components/BatteryStatusControl/constants';
import useTranslation from 'pages/MaintenancePage/hooks/useTranslation';

import BatteryDrawerTabBody from './components/BatteryDrawerTabBody';
import { BATTERY_TABS, BATTERY_TAB_MIN_PHASE, TEST_ID } from './constants';
import type { BatteryTabKey, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const BatteryDetailDrawer: FunctionComponent<Props> = ({
    activeTab,
    battery,
    className = '',
    cycleInterval,
    droneNames,
    isAdmin,
    isOpen,
    onClose,
    onSetStatus,
    onTabChange,
    phase,
    testId = TEST_ID
}) => {
    const t = useTranslation();
    const openClass = isOpen ? styles.isOpen : '';
    const visibleTabs = BATTERY_TABS.filter(tab => phase >= BATTERY_TAB_MIN_PHASE[tab]);
    const tabLabels: Record<BatteryTabKey, string> = {
        electricals: t.ELECTRICALS_TAB,
        firmware: t.FIRMWARE_TAB,
        overview: t.OVERVIEW_TAB
    };
    const statusLabels = { aging: t.BATTERY_STATUS_AGING, ok: t.BATTERY_STATUS_OK };

    return (
        <Wrapper className={className}>
            <Wrapper className={`${styles.scrim} ${openClass}`} onClick={onClose} />

            <VerticalContainer className={`${styles.drawer} ${openClass}`} testId={testId}>
                {battery && (
                    <VerticalContainer className={styles.drawerInner}>
                        <HorizontalContainer className={styles.drawerHead}>
                            <VerticalContainer className={styles.drawerTitle}>
                                <Wrapper className={styles.drawerName}>{battery.name}</Wrapper>

                                <Wrapper className={styles.drawerSerial}>
                                    {battery.serialNumber}
                                </Wrapper>
                            </VerticalContainer>

                            <BatteryStatusPill
                                label={statusLabels[battery.status]}
                                status={battery.status}
                            />

                            <BatteryStatusControl
                                batteryId={battery.id}
                                isAdmin={isAdmin}
                                onSetStatus={onSetStatus}
                                status={battery.lifecycleStatus ?? DEFAULT_LIFECYCLE_STATUS}
                            />

                            <Wrapper className={styles.drawerClose} onClick={onClose}>
                                ×
                            </Wrapper>
                        </HorizontalContainer>

                        <HorizontalContainer className={styles.drawerTabs}>
                            {visibleTabs.map(tab => {
                                const activeTabClass = activeTab === tab ? styles.isActive : '';

                                return (
                                    <Wrapper
                                        className={`${styles.drawerTab} ${activeTabClass}`}
                                        key={tab}
                                        onClick={() => onTabChange(tab)}>
                                        {tabLabels[tab]}
                                    </Wrapper>
                                );
                            })}
                        </HorizontalContainer>

                        <BatteryDrawerTabBody
                            activeTab={activeTab}
                            battery={battery}
                            cycleInterval={cycleInterval}
                            droneNames={droneNames}
                        />
                    </VerticalContainer>
                )}
            </VerticalContainer>
        </Wrapper>
    );
};

export default BatteryDetailDrawer;
